/**
 * @fileoverview "Grow thinks this is bee balm (Monarda), a perennial flower in
 * the mint family. File it under Perennial flower?" with a one-tap Yes: what
 * Grow makes of a plant it could not place, said plainly, where it was read
 * (a seed packet on the review sheet or the Add photos card, a photo guess,
 * or "Describe it" in the plant search).
 *
 * The sentence is built from what is known: the name, its botanical name
 * when it says something more, what kind of plant it is (a broad flower
 * parent's hardiness, else the kind of the plant it goes under) and its
 * family, in English as gardeners say it ("mint") and in Spanish by its Latin
 * name ("de la familia Lamiaceae"). A part not known is left out, never
 * guessed. One Grow has by another name ("coriander") asks to add it as
 * that plant instead.
 *
 * Where: PacketReview, PacketPile, PileCard, DescribePlant.
 * API: none; the caller files it on Yes.
 * Words (en/es): unknown.* (thinks, thinksKind, thinksFamily,
 * thinksKindFamily, nameLatin, fileUnder, fileAs, yes, kinds.*, broad.*,
 * broadParent.*).
 *
 * @module UnknownPlantCard
 */

import React from 'react';
import { Sprout } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { PlantSuggestion } from '@/shared/api/plantNames';
import { parentLabel, suggestionSentence } from './suggestionWords';

/** What the card is given. */
interface UnknownPlantCardProps {
  /** What Grow thinks it is. */
  suggestion: PlantSuggestion;
  /** Yes: the caller files it. */
  onYes: () => void;
  /** True while it is being filed (the button spins). */
  busy?: boolean;
  /** True while anything else on the page is saving. */
  disabled?: boolean;
  /** Why filing failed, already worded; null or absent when it has not. */
  error?: React.ReactNode;
  /** Smaller, for a packet's line in a list. */
  compact?: boolean;
  /** Anything after the Yes, such as "Choose another". */
  children?: React.ReactNode;
}

/**
 * The card: the sentence, the question and Yes.
 *
 * @param props - UnknownPlantCardProps.
 */
export const UnknownPlantCard: React.FC<UnknownPlantCardProps> = ({ suggestion, onYes, busy = false, disabled = false, error, compact = false, children }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const parent = parentLabel(t, suggestion);
  return (
    <div className={cn('rounded-sm border border-primary/30 bg-primary/5', compact ? 'mt-1 p-2' : 'p-3')} role="group" aria-label={t('unknown.label')}>
      <p className="flex gap-2 text-sm leading-snug">
        <Sprout className={cn('mt-0.5 shrink-0 text-primary', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden="true" />
        <span>
          {suggestionSentence(t, suggestion, language)}{' '}
          <span className="font-medium">{suggestion.known ? t('unknown.fileAs', { parent }) : t('unknown.fileUnder', { parent })}</span>
        </span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button type="button" size="sm" className="h-11 px-5" onClick={onYes} disabled={busy || disabled}>
          {busy ? <Spinner size={16} color="currentColor" /> : t('unknown.yes')}
        </Button>
        {children}
      </div>
      {error && <p className="mt-1.5 text-sm font-medium text-destructive" role="alert">{error}</p>}
    </div>
  );
};
